import { projectId, publicAnonKey } from './supabase/info';

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-702f9d0a`;

// Helper function to make API requests
async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const url = `${API_BASE}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${publicAnonKey}`,
    ...options.headers,
  };

  try {
    const response = await fetch(url, { ...options, headers });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
}

// ==================== CATEGORIES ====================

export async function getCategories() {
  const data = await apiRequest('/categories');
  return data.categories || [];
}

export async function getCategory(id: string) {
  const data = await apiRequest(`/categories/${id}`);
  return data.category;
}

export async function saveCategory(category: any) {
  const data = await apiRequest('/categories', {
    method: 'POST',
    body: JSON.stringify(category),
  });
  return data.category;
}

export async function deleteCategory(id: string) {
  await apiRequest(`/categories/${id}`, { method: 'DELETE' });
}

// ==================== TASKS ====================

export async function getTasks() {
  const data = await apiRequest('/tasks');
  return data.tasks || [];
}

export async function getTask(id: string) {
  const data = await apiRequest(`/tasks/${id}`);
  return data.task;
}

export async function saveTask(task: any) {
  const data = await apiRequest('/tasks', {
    method: 'POST',
    body: JSON.stringify(task),
  });
  return data.task;
}

export async function deleteTask(id: string) {
  await apiRequest(`/tasks/${id}`, { method: 'DELETE' });
}

// ==================== PROJECTS ====================

export async function getProjects() {
  const data = await apiRequest('/projects');
  return data.projects || [];
}

export async function getProject(id: string) {
  const data = await apiRequest(`/projects/${id}`);
  return data.project;
}

export async function saveProject(project: any) {
  const data = await apiRequest('/projects', {
    method: 'POST',
    body: JSON.stringify(project),
  });
  return data.project;
}

export async function deleteProject(id: string) {
  await apiRequest(`/projects/${id}`, { method: 'DELETE' });
}

// ==================== SUBMISSIONS ====================

export async function getSubmissions() {
  const data = await apiRequest('/submissions');
  return data.submissions || [];
}

export async function getSubmissionsByProject(projectId: string) {
  const data = await apiRequest(`/submissions/project/${projectId}`);
  return data.submissions || [];
}

export async function saveSubmission(submission: any) {
  const data = await apiRequest('/submissions', {
    method: 'POST',
    body: JSON.stringify(submission),
  });
  return data.submission;
}

export async function deleteSubmission(id: string) {
  await apiRequest(`/submissions/${id}`, { method: 'DELETE' });
}

// ==================== VENDORS ====================

export async function getVendors() {
  const data = await apiRequest('/vendors');
  return data.vendors || [];
}

export async function saveVendor(vendor: any) {
  const data = await apiRequest('/vendors', {
    method: 'POST',
    body: JSON.stringify(vendor),
  });
  return data.vendor;
}

export async function deleteVendor(id: string) {
  await apiRequest(`/vendors/${id}`, { method: 'DELETE' });
}

// ==================== PIN CODES ====================

export async function getPINCodes() {
  const data = await apiRequest('/pincodes');
  return data.pincodes || [];
}

export async function validatePINCode(code: string) {
  const data = await apiRequest('/pincodes/validate', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  return data;
}

export async function savePINCode(pincode: any) {
  const data = await apiRequest('/pincodes', {
    method: 'POST',
    body: JSON.stringify(pincode),
  });
  return data.pincode;
}

export async function deletePINCode(id: string) {
  await apiRequest(`/pincodes/${id}`, { method: 'DELETE' });
}

// ==================== SUPERVISOR ACCESS ====================

export async function getSupervisorAccess() {
  const data = await apiRequest('/supervisor-access');
  return data.access;
}

export async function saveSupervisorAccess(access: any) {
  const data = await apiRequest('/supervisor-access', {
    method: 'POST',
    body: JSON.stringify(access),
  });
  return data.access;
}

// ==================== MICROSOFT ACCOUNTS ====================

export async function getMicrosoftAccounts() {
  const data = await apiRequest('/microsoft-accounts');
  return data.accounts || [];
}

export async function authorizeMicrosoftAccount(email: string) {
  try {
    const data = await apiRequest('/microsoft-accounts/validate', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return data;
  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : 'Unable to validate Microsoft account',
    };
  }
}

export async function saveMicrosoftAccount(account: any) {
  const data = await apiRequest('/microsoft-accounts', {
    method: 'POST',
    body: JSON.stringify(account),
  });
  return data.account;
}

export async function deleteMicrosoftAccount(id: string) {
  await apiRequest(`/microsoft-accounts/${id}`, { method: 'DELETE' });
}

// ==================== EMPLOYEES ====================

export async function getEmployees() {
  const data = await apiRequest('/employees');
  return data.employees || [];
}

export async function saveEmployee(employee: any) {
  const data = await apiRequest('/employees', {
    method: 'POST',
    body: JSON.stringify(employee),
  });
  return data.employee;
}

export async function deleteEmployee(id: string) {
  await apiRequest(`/employees/${id}`, { method: 'DELETE' });
}

export async function validateEmployeeAccount(email: string) {
  try {
    const data = await apiRequest('/employees/validate', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return data;
  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : 'Unable to validate employee account',
    };
  }
}

// ==================== TIME ENTRIES ====================

export async function getTimeEntries() {
  const data = await apiRequest('/time-entries');
  return data.timeEntries || [];
}

export async function saveTimeEntry(timeEntry: any) {
  const data = await apiRequest('/time-entries', {
    method: 'POST',
    body: JSON.stringify(timeEntry),
  });
  return data.timeEntry;
}

// ==================== INITIALIZATION ====================

export async function initializeDatabase(initialData: {
  categories?: any[];
  tasks?: any[];
  projects?: any[];
  vendors?: any[];
  pincodes?: any[];
  supervisorAccess?: any;
  employees?: any[];
}) {
  const data = await apiRequest('/initialize', {
    method: 'POST',
    body: JSON.stringify(initialData),
  });
  return data;
}
